import React from 'react'
import { Card, Row, Col, Progress, Statistic, Alert } from 'antd'
import { useQuery } from '@tanstack/react-query'
import { api } from '@services/api'

const Risk: React.FC = () => {
  const { data: riskMetrics, isLoading } = useQuery({
    queryKey: ['risk', 'metrics'],
    queryFn: api.risk.getMetrics,
    refetchInterval: 10000,
  })

  const getRiskColor = (score: number) => {
    if (score < 30) return '#52c41a'
    if (score < 60) return '#faad14'
    if (score < 80) return '#ff7875'
    return '#ff4d4f'
  }

  const getProgressStatus = (score: number): "success" | "normal" | "exception" => {
    if (score < 30) return 'success'
    if (score < 80) return 'normal'
    return 'exception'
  }

  return (
    <div className="page-inner">
      {riskMetrics?.warnings && riskMetrics.warnings.length > 0 && (
        <Alert
          message="风险警告"
          description={
            <ul>
              {riskMetrics.warnings.map((warning: string, index: number) => (
                <li key={index}>{warning}</li>
              ))}
            </ul>
          }
          type="error"
          showIcon
          style={{ marginBottom: 24 }}
        />
      )}

      <Row gutter={[24, 24]}>
        <Col span={24}>
          <Card title="风险评分（数值越低越安全）" loading={isLoading}>
            <div style={{ textAlign: 'center' }}>
              <Progress
                type="circle"
                percent={riskMetrics?.risk_score || 0}
                size={200}
                strokeColor={getRiskColor(riskMetrics?.risk_score || 0)}
                status={getProgressStatus(riskMetrics?.risk_score || 0)}
                format={(percent) => `${percent}`}
              />
              <div style={{ marginTop: 16, fontSize: 18, fontWeight: 'bold' }}>
                当前风险等级: {
                  (riskMetrics?.risk_score || 0) < 30 ? '低风险' :
                  (riskMetrics?.risk_score || 0) < 60 ? '中等风险' :
                  (riskMetrics?.risk_score || 0) < 80 ? '高风险' : '极高风险'
                }
              </div>
            </div>
          </Card>
        </Col>

        <Col span={12}>
          <Card title="保证金监控" loading={isLoading}>
            <Statistic
              // backend margin_ratio is 0..1 (margin_used / balance)
              title="保证金占用率"
              value={(riskMetrics?.margin_ratio || 0) * 100}
              precision={2}
              suffix="%"
              valueStyle={{ 
                color: (riskMetrics?.margin_ratio || 0) < 0.1 ? '#3f8600' :
                       (riskMetrics?.margin_ratio || 0) < 0.3 ? '#faad14' : '#cf1322',
                fontSize: 32
              }}
            />
            <div style={{ marginTop: 16 }}>
              <Progress
                percent={Math.min(((riskMetrics?.margin_ratio || 0) / 0.3) * 100, 100)}
                status={(riskMetrics?.margin_ratio || 0) < 0.3 ? 'success' : 'exception'}
                showInfo={false}
              />
              <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
                提示: 10% 以下较安全，30% 以上需警惕
              </div>
            </div>
          </Card>
        </Col>

        <Col span={12}>
          <Card title="杠杆使用" loading={isLoading}>
            <Statistic
              title="杠杆倍数"
              value={riskMetrics?.leverage_usage || 0}
              precision={1}
              suffix="x"
              valueStyle={{ fontSize: 32 }}
            />
            <div style={{ marginTop: 16 }}>
              <Progress
                percent={Math.min(((riskMetrics?.leverage_usage || 0) / 20) * 100, 100)}
                status={(riskMetrics?.leverage_usage || 0) < 15 ? 'success' : 'exception'}
                showInfo={false}
              />
              <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
                建议上限: 15x | 系统上限: 20x
              </div>
            </div>
          </Card>
        </Col>

        <Col span={8}>
          <Card title="最大回撤" loading={isLoading}>
            <Statistic
              title="当前回撤"
              value={Math.abs(riskMetrics?.max_drawdown || 0)}
              precision={2}
              suffix="%"
              valueStyle={{ 
                color: Math.abs(riskMetrics?.max_drawdown || 0) < 10 ? '#3f8600' : '#cf1322',
                fontSize: 24
              }}
            />
          </Card>
        </Col>

        <Col span={8}>
          <Card title="风险敞口" loading={isLoading}>
            <Statistic
              title="总敞口"
              value={riskMetrics?.total_exposure || 0}
              precision={2}
              suffix="USDT"
              valueStyle={{ fontSize: 24 }}
            />
          </Card>
        </Col>

        <Col span={8}>
          <Card title="VaR (95%)" loading={isLoading}>
            <Statistic
              title="潜在损失"
              value={Math.abs(riskMetrics?.var_95 || 0)}
              precision={2}
              suffix="USDT"
              valueStyle={{ 
                color: '#cf1322',
                fontSize: 24
              }}
            />
          </Card>
        </Col>
      </Row>
    </div>
  )
}

export default Risk

